/**
 * usePatientPhotos — resuelve la foto de perfil ACTUAL de un lote de pacientes
 * por su Firebase UID (users/{uid}.fotoURL), vía la CF portal_obtenerFotosPacientes.
 *
 * Por qué: la foto vive en users/* (subida desde la app móvil), colección que el
 * portal no puede leer por reglas. Además, las consultas viejas/finalizadas no
 * tienen `pacienteFotoURL` denormalizado (el trigger solo refresca las activas).
 * Este hook permite mostrar la foto actual en listas (historial, cola) sin
 * depender del snapshot denormalizado.
 *
 * Uso: pasar los pacienteUid visibles; devuelve un mapa { uid: url|null }.
 * Cachea por uid entre llamadas y solo pide los que faltan.
 */
import { useEffect, useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';

const obtenerFotos = httpsCallable<
  { uids: string[] },
  { fotos: Record<string, string | null> }
>(functions, 'portal_obtenerFotosPacientes');

export function usePatientPhotos(uids: (string | undefined | null)[]): Record<string, string | null> {
  const [fotos, setFotos] = useState<Record<string, string | null>>({});
  // uids ya resueltos (incluye los que dieron null) para no re-pedirlos.
  const resueltosRef = useRef<Set<string>>(new Set());

  // Clave estable de los uids únicos presentes para disparar el efecto.
  const unicos = Array.from(new Set(uids.filter((u): u is string => !!u)));
  const clave = unicos.slice().sort().join(',');

  useEffect(() => {
    const faltantes = unicos.filter((u) => !resueltosRef.current.has(u));
    if (faltantes.length === 0) return;
    let cancel = false;
    obtenerFotos({ uids: faltantes })
      .then((res) => {
        if (cancel) return;
        const nuevos = res.data?.fotos ?? {};
        faltantes.forEach((u) => resueltosRef.current.add(u));
        setFotos((prev) => ({ ...prev, ...nuevos }));
      })
      .catch(() => {
        // best-effort: si falla, las filas caen a iniciales.
      });
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clave]);

  return fotos;
}
